import { useMemo, FC } from 'react';
import { Stack, Grid2 as Grid, Box, Typography, styled } from '@mui/material';
import { useShowContext } from 'framework/show';
import {
  useEmployeeService,
  EMPLOYEE_WORKPLACE_LOCATION,
  READ_EMPLOYEE_MAIN_PERSONAL_INFO,
  EMPLOYEE_TYPES_ALIAS,
  WorkingStatusChip,
} from 'domain/shared/employee';
import { useAuthorization } from 'framework/authorization';
import { EMPLOYEE_CONTEXT } from 'domain/people/employee/EmployeeContext';
import { NewBadge } from 'framework/ui/ui-builder';
import { CountryBadge } from 'framework/form';
import EmployeeAvatar from '../../dashboard/summary/EmployeeAvatar';
import { EmployeePositionWithSpecialization } from './index';
import { ContactEmailField, ContactPhoneField } from 'framework/ui/layout';
import { BirthdayInfo } from 'framework/ui/BirthdayInfo';
import MentorInfo from '../../shared/MentorInfo';
import LinkIconButtonEmployeeTeams from '../../shared/button/LinkIconButtonEmployeeTeams';
import ContractDuractionField from '../../shared/field/ContractDuractionField/ContractDuractionField';
import ContractorBadge from 'domain/shared/employee/badges/ContractorBadge';
import InternBadge from 'domain/people/employee/shared/badges/InternBadge';
import ActiveProbationBadge from 'domain/people/employee/shared/badges/ActiveProbationBadge';

const StyledGrid = styled(Grid)(() => ({
  fontSize: '12px',
  '.MuiTypography-root': {
    fontSize: '12px',
  },
  '.field__wrapper > .MuiTypography-root': {
    fontSize: '12px',
  },
}));

interface EmployeeProfileInfoProps {
  canEditDashboard: boolean;
}

const EmployeeProfileInfo: FC<EmployeeProfileInfoProps> = ({ canEditDashboard }) => {
  const { data: employee } = useShowContext();
  const { getFullName, checkIsNewEmployee } = useEmployeeService();
  const { isGranted } = useAuthorization();

  const avatarId = useMemo(() => {
    const photosIds = employee?.getValueBySource('extendedInformation.photosIds');
    return photosIds && photosIds.length > 0 ? photosIds[0] : null;
  }, [employee]);

  const avatarSection = (
    <Box sx={{ width: '100px', height: '100px', '& > img': { width: '100px', height: '100px', objectFit: 'cover' } }}>
      <EmployeeAvatar avatarId={avatarId} />
    </Box>
  );

  const fullName = employee
    ? getFullName({
        firstName: employee.getValueBySource('nameEn.firstName'),
        lastName: employee.getValueBySource('nameEn.lastName'),
        maidenName: employee.getValueBySource('nameEn.maidenName'),
      })
    : '';

  const badges = employee
    ? [
        checkIsNewEmployee(employee.getValueBySource('initializationState')) &&
        employee.getValueBySource('employeeType') !== EMPLOYEE_TYPES_ALIAS.INTERN ? (
          <NewBadge key="new-badge" />
        ) : null,
        employee.getValueBySource('employeeType') === EMPLOYEE_TYPES_ALIAS.CONTRACTOR ? (
          <ContractorBadge key="contractor-badge" />
        ) : null,
        employee.getValueBySource('employeeType') === EMPLOYEE_TYPES_ALIAS.INTERN ? (
          <InternBadge key="intern-badge" />
        ) : null,
        employee.getValueBySource('probation.onProbation') ? <ActiveProbationBadge key="probation-badge" /> : null,
      ].filter(Boolean)
    : [];

  const country = employee?.getValueBySource('country') || '';
  const workplaceLocation = employee?.getValueBySource('extendedInformation.hrInformation.workplaceLocation');
  const roomNumber = employee?.getValueBySource('extendedInformation.hrInformation.roomNumber');
  const deskLocation = employee?.getValueBySource('extendedInformation.hrInformation.deskLocation');
  const positionId = employee?.getValueBySource('positionId');
  const specializationInfo = employee?.getValueBySource('specializationInfo');

  const primaryDetails = (
    <Stack gap={1} key="contact-details" alignItems="flex-start">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          columnGap: '4px',
          marginBottom: '4px',
          '& > h4': { color: 'primary.dark', fontSize: '14px', lineHeight: '1lh' },
          '& > .MuiChip-root': {
            marginLeft: 0,
            '& > .MuiChip-label': { fontSize: '10px' },
          },
        }}
      >
        <Typography variant="h4">{fullName}</Typography>
        {badges.map(badge => badge)}
        {canEditDashboard && <WorkingStatusChip source="workingStatus" />}
        {canEditDashboard && <LinkIconButtonEmployeeTeams employeeEmailSource="workingEmail" />}
      </Box>

      <EmployeePositionWithSpecialization positionId={positionId} specializationInfo={specializationInfo} />
      {canEditDashboard && (
        <BirthdayInfo
          dateOfBirth={employee?.getValueBySource('extendedInformation.dateOfBirth')}
          sx={{ color: 'text.secondary' }}
        />
      )}
      {canEditDashboard && <ContractDuractionField sxBadge={{ m: 0 }} сontractsSource="сontracts" />}
    </Stack>
  );

  const workingDetailsSection = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '& .MuiTypography-root': { fontSize: '12px', lineHeight: '12px', whiteSpace: 'nowrap' },
      }}
    >
      <CountryBadge country={country} />
      <Box display="flex" flexWrap="wrap">
        <Typography variant="body1">{EMPLOYEE_WORKPLACE_LOCATION[workplaceLocation]}</Typography>
        {roomNumber && <Typography variant="body1">, room {roomNumber}</Typography>}
        {deskLocation && <Typography variant="body1">|{deskLocation}</Typography>}
      </Box>
    </Box>
  );

  const contactSection = [
    isGranted(READ_EMPLOYEE_MAIN_PERSONAL_INFO, EMPLOYEE_CONTEXT) && (
      <Stack
        spacing={1}
        key="contact-info"
        sx={{
          alignItems: 'flex-start',
          '& a': { fontSize: '12px' },
          '& svg': { width: '16px', height: '16px' },
          '& .field__wrapper > div': { lineHeight: '14px' },
        }}
      >
        <ContactEmailField source="workingEmail" titleAccess="Working email" />
        <ContactEmailField source="extendedInformation.personalEmail" titleAccess="Personal email" />
        <ContactPhoneField source="extendedInformation.personalPhoneNumber" titleAccess="Personal phone number" />
      </Stack>
    ),
  ].filter(Boolean);

  return (
    <Grid display="flex" gap={2} pt={0.5} justifyContent="space-between" maxWidth="700px">
      <StyledGrid display="flex" gap={2} minWidth="250px">
        {avatarSection}
        {primaryDetails}
      </StyledGrid>
      <StyledGrid
        container
        justifyContent="space-between"
        alignItems="start"
        mb={4}
        gap={2}
        minWidth="100px"
        flexDirection="column"
      >
        <Grid size={12}>{workingDetailsSection}</Grid>
        <Grid size={12}>{contactSection}</Grid>
        {!canEditDashboard && (
          <MentorInfo
            sxWrap={{
              mt: 0,
              span: { fontSize: '12px' },
            }}
          />
        )}
      </StyledGrid>
    </Grid>
  );
};

export default EmployeeProfileInfo;
